import NotificationService from './dialogs/notifications';
import { MediaUserRole } from './liveMedia.service';
import loggerService from './logger.service';

import type { ICameraVideoTrack } from '@/services/agora.types';

import { AGORA } from '@/constants';
import store from '@/store';

export enum SPEAKERS_REMOTE_STREAM_COUNT {
  LOW = 8,
  MEDIUM = 12,
  LARGE = 16
}

export enum SPEAKERS_1080P_THRESHOLDS {
  SIX_SPEAKERS = 6, // 1080p
  TEN_SPEAKERS = 10, // 720p
  SIXTEEN_SPEAKERS = 16 // 480p
}

export interface VideoEncoderConfiguration {
  name: string;
  value: string;
}

export const VideoQualities: VideoEncoderConfiguration[] = [
  { name: 'POOR', value: '180p_1' }, // 15fps
  { name: 'LOWEST', value: '180p_1' }, // 15fps
  { name: 'LOW', value: '240p_4' }, // 15fps
  { name: 'MEDIUM', value: '360p_9' }, // 15fps
  { name: 'REGULAR', value: '480p_8' }, // 15fps
  { name: 'HIGH', value: '720p_1' } // 15fps
];

export const DEFAULT_VIDEO_QUALITY = VideoQualities[4]; // Fallback constant
export const MAX_VIDEO_QUALITY_INDEX_WITH_VB = 4;

// Get the appropriate default video quality based on feature flag
function getDefaultVideoQualityConstant(): VideoEncoderConfiguration {
  const useFullHD = store.getters['event/useFullHD'];

  // Ensure 1080p option exists in VideoQualities array when Full HD is enabled
  const has1080p = VideoQualities.some(vq => vq.name === 'ULTRA_HIGH');
  if (useFullHD && !has1080p) {
    VideoQualities.push({ name: 'ULTRA_HIGH', value: '1080p_1' }); // 15fps
  }

  return useFullHD ? VideoQualities[6] : VideoQualities[4];
}

export const updateDefaultVideoQualityConstant = () => {
  getDefaultVideoQualityConstant();
};

type VideoQualityHandlerMeta = {
  quality: VideoEncoderConfiguration;
  interval: NodeJS.Timeout;
  warned: boolean;
};

export const videoMetaMap = new Map<string, VideoQualityHandlerMeta>();

// Calculate ideal quality based on speaker count
function calculateIdealQuality(channelName: string): VideoEncoderConfiguration | undefined {
  const { remoteStreams, userRole } = store.state.mediaroom?.mediaRooms?.[channelName] ?? {};
  if (!remoteStreams) {
    return;
  }

  const remoteStreamCount = Object.keys(remoteStreams).length;
  const isCurrentUserParticipating = userRole === MediaUserRole.host || store.state.mediaroom.localTracks?.length > 0;
  const totalParticipants = remoteStreamCount + (isCurrentUserParticipating ? 1 : 0);
  const streamCount = totalParticipants;

  if (store.getters['event/useFullHD']) {
    loggerService.log('[VIDEO QUALITY] Calculating ideal quality', {
      streamCount
    });

    // Regular layout thresholds
    if (streamCount <= SPEAKERS_1080P_THRESHOLDS.SIX_SPEAKERS) {
      return VideoQualities[6];
    } // 1080p
    if (streamCount <= SPEAKERS_1080P_THRESHOLDS.TEN_SPEAKERS) {
      return VideoQualities[5];
    } // 720p
    if (streamCount <= SPEAKERS_1080P_THRESHOLDS.SIXTEEN_SPEAKERS) {
      return VideoQualities[4];
    } // 480p
    return VideoQualities[2]; // 240p
  }

  // Legacy logic
  if (streamCount < SPEAKERS_REMOTE_STREAM_COUNT.LOW) {
    return;
  }
  if (streamCount < SPEAKERS_REMOTE_STREAM_COUNT.MEDIUM) {
    return VideoQualities[2];
  }
  if (streamCount < SPEAKERS_REMOTE_STREAM_COUNT.LARGE) {
    return VideoQualities[1];
  }
  return VideoQualities[0];
}

// Apply restrictions on top of ideal quality
function applyQualityRestrictions(
  idealQuality: VideoEncoderConfiguration | undefined,
  channelName: string
): VideoEncoderConfiguration | undefined {
  if (!idealQuality) {
    return idealQuality;
  }
  //If Content is shared no layout is not flex then we want to force 240p otherwise we return the ideal quality
  if (channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    return VideoQualities[4]; // 480p
  }

  const selectedVideoQuality = store.state.user.selected_video_quality;
  if ((selectedVideoQuality as any)?.name !== 'AUTO') {
    loggerService.log(
      '[VIDEO QUALITY] Using configurable video quality, so not handling video quality based on calculations'
    );
    return;
  }

  return idealQuality;
}

function processVideoQualityForTrack(track: ICameraVideoTrack, channelName: string) {
  const trackId = track.getTrackId();
  const mediaroom = store.state.mediaroom.mediaRooms?.[channelName];
  if (!mediaroom?.client) {
    return;
  }
  const localVideoStats = mediaroom.client.getLocalVideoStats();

  const { sendFrameRate, sendPacketsLost } = localVideoStats;

  if (!sendFrameRate || sendFrameRate <= 0) {
    return;
  }

  const meta = videoMetaMap.get(trackId);

  loggerService.log(
    '[VIDEO QUALITY] Current video quality status',
    JSON.stringify({
      sendFrameRate,
      sendPacketsLost,
      currentQuality: meta?.quality
    })
  );

  if (!meta) {
    console.warn('No meta set to track video. Setting...');
    return;
  }

  if (channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    const maxBackstage = VideoQualities.findIndex(vq => vq.name === 'REGULAR');
    const currentQualityIndex = VideoQualities.findIndex(
      (vq: VideoEncoderConfiguration) => vq.value === meta.quality.value
    );

    if (currentQualityIndex >= maxBackstage) {
      return;
    }
  }

  if (meta.quality.name === 'ULTRA_HIGH' && !store.getters['event/useFullHD']) {
    loggerService.warn('[VIDEO QUALITY] 1080p is disabled - downgrading to 720p');
    downgradeVideoQuality(track, VideoQualities.findIndex(vq => vq.name === 'HIGH') + 1);
    return;
  }

  const selectedVideoQuality = store.state.user.selected_video_quality;

  if ((selectedVideoQuality as any)?.name !== 'AUTO') {
    loggerService.log(
      '[VIDEO QUALITY] Using configurable video quality, so not handling video quality based on calculations'
    );
    return;
  }

  const currentQualityIndex = VideoQualities.findIndex(
    (vq: VideoEncoderConfiguration) => vq.value === meta.quality.value
  );

  const fixMaxVideoQualityWithVb = store.state.event?.organization?.fix_max_video_quality_with_vb;

  if (
    store.state.mediaroom.currentBackgroundFilter &&
    currentQualityIndex > MAX_VIDEO_QUALITY_INDEX_WITH_VB &&
    fixMaxVideoQualityWithVb &&
    !store.getters['event/useFullHD']
  ) {
    loggerService.warn(
      "[VIDEO QUALITY] VB video quality downgrade - won't upgrade beyond 480p because VB is applied",
      currentQualityIndex
    );
    downgradeVideoQuality(track, MAX_VIDEO_QUALITY_INDEX_WITH_VB + 1);
    return;
  }

  if (sendFrameRate >= AGORA.GOOD_FPS) {
    upgradeVideoQuality(track, currentQualityIndex);
    return;
  }
  if (sendFrameRate < AGORA.FPS_FALL_THRESHOLD) {
    downgradeVideoQuality(track, currentQualityIndex);
  }
}

export function handleVideoQualities(
  track: ICameraVideoTrack,
  channelName: string,
  videoQualityValue: VideoEncoderConfiguration['value']
) {
  const trackId = track.getTrackId();
  const interval = setInterval(() => {
    processVideoQualityForTrack(track, channelName);
  }, 30000);

  videoMetaMap.set(trackId, {
    quality: VideoQualities.find(vq => vq.value === videoQualityValue) || getDefaultVideoQualityConstant(),
    interval,
    warned: false
  });
}

export function getDefaultVideoQuality(channelName: string) {
  if (channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    return VideoQualities[4].value;
  }

  const isBroadcast = store.state.route.path.includes('/broadcast');
  const { room_video_quality } = store.state.event.event;

  let selectedVideoQuality = getDefaultVideoQualityConstant();
  const selectedVideoQualityName =
    store.state.user?.selected_video_quality?.name || store.state.event.event?.video_quality;
  if (selectedVideoQualityName) {
    selectedVideoQuality =
      VideoQualities.find(vq => vq.name === selectedVideoQualityName) || getDefaultVideoQualityConstant();
  }

  let defaultQuality = (isBroadcast ? selectedVideoQuality : room_video_quality) || getDefaultVideoQualityConstant();
  defaultQuality = getVideoQualityBasedOnRemoteStreamCount(channelName) || defaultQuality;
  return defaultQuality.value;
}

export function getVideoQualityBasedOnRemoteStreamCount(channelName: string): undefined | VideoEncoderConfiguration {
  const idealQuality = calculateIdealQuality(channelName);
  return applyQualityRestrictions(idealQuality, channelName);
}

export function setVideoQualityToTrack(track: ICameraVideoTrack, quality: VideoEncoderConfiguration) {
  const meta = videoMetaMap.get(track.getTrackId());
  if (!meta) {
    return;
  }
  meta.quality = quality;
  track.setEncoderConfiguration(quality.value);
}

function upgradeVideoQuality(track: ICameraVideoTrack, currentQualityIndex: number) {
  const meta = videoMetaMap.get(track.getTrackId());
  const nextQualityIndex = currentQualityIndex + 1;
  const fixMaxVideoQualityWithVb = store.state.event?.organization?.fix_max_video_quality_with_vb;
  const useFullHD = store.getters['event/useFullHD'];

  if (!meta) {
    return;
  }

  if (currentQualityIndex === VideoQualities.length - 1) {
    return;
  }

  // Check if next quality would be 1080p and Full HD is not enabled
  const nextQuality = VideoQualities[nextQualityIndex];
  if (nextQuality?.name === 'ULTRA_HIGH' && !useFullHD) {
    loggerService.warn('[VIDEO QUALITY] 1080p not enabled - not upgrading to ULTRA_HIGH', currentQualityIndex);
    return;
  }

  if (
    store.state.mediaroom.currentBackgroundFilter &&
    nextQualityIndex > MAX_VIDEO_QUALITY_INDEX_WITH_VB &&
    fixMaxVideoQualityWithVb &&
    !useFullHD
  ) {
    loggerService.warn('[VIDEO QUALITY] Video quality is already at max with VB - not upgrading', currentQualityIndex);
    return;
  }

  // eslint-disable-next-line no-console
  console.warn('Upgrading video quality...', nextQuality.value);

  meta.quality = nextQuality;
  track.setEncoderConfiguration(nextQuality.value);
}

function downgradeVideoQuality(track: ICameraVideoTrack, currentQualityIndex: number) {
  const meta = videoMetaMap.get(track.getTrackId());

  if (!meta) {
    return;
  }

  if (currentQualityIndex === 1) {
    if (!meta.warned && AGORA.SHOW_AV_NOTIFICATIONS) {
      NotificationService.toast(
        '@@translate@@ Warning',
        "@@translate@@ We've detected a problem with your video. We recommend trying to close other browser tabs or other applications running on your device and checking/upgrading your internet connection to resolve this issue.",
        'warning',
        '',
        AGORA.AV_NOTIFICATION_TIMEOUT
      );
      meta.warned = true;
    }
    return;
  }

  if (currentQualityIndex === 0) {
    return;
  }

  const nextQuality = VideoQualities[currentQualityIndex - 1];

  console.warn('Downgrading video quality...', nextQuality.value);

  meta.quality = nextQuality;
  track.setEncoderConfiguration(nextQuality.value);
}

export function getCurrentAutoQuality(channelName: string): VideoEncoderConfiguration | undefined {
  // Backstage always uses 480p
  if (channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    return VideoQualities[4]; // 480p
  }

  const idealQuality = calculateIdealQuality(channelName);
  const finalQuality = applyQualityRestrictions(idealQuality, channelName);

  return finalQuality;
}
